import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Trash2, Copy, Eye, EyeOff, Sparkles, Monitor, Tablet, Smartphone, Settings, ExternalLink, Loader2, Check, ChevronLeft, Languages, PanelLeft, PanelRight, Globe, LayoutTemplate, AlertTriangle, RefreshCw, Store, Undo2, Redo2, History, RotateCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang, pick } from '@/lib/i18n';
import { api, errMsg, pollAiJob } from '@/lib/api';
import { SECTION_DEFS, SECTION_ORDER, STORE_SECTION_TYPES, newSection, rid } from '@/lib/sectionDefs';
import { AppShell } from '@/components/AppShell';
import { ModuleIcon } from '@/components/ModuleIcon';
import { SiteRenderer } from '@/components/renderer/SiteRenderer';
import { SectionInspector } from '@/components/editor/SectionInspector';
import { AiGenerateDialog } from '@/components/editor/AiGenerateDialog';
import { TemplateGallery, StartDialog } from '@/components/editor/TemplateGallery';
import { SortablePages, PageSettingsDialog, VersionHistoryDialog, CopySectionDialog } from '@/components/editor/PageTools';
import { DeviceFrame } from '@/components/editor/DeviceFrame';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { menuFromSections } from '@/lib/siteTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const SortableRow = ({ section, selected, onSelect, onDelete, onDuplicate, onToggle, lang }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const def = SECTION_DEFS[section.type];
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`group flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 text-sm transition-colors ${selected ? 'border-[hsl(var(--pillot-mint))] bg-secondary/50' : 'border-border hover:bg-muted'} ${isDragging ? 'opacity-60 shadow-lg' : ''} ${section.hidden ? 'opacity-50' : ''}`} data-testid={`website-editor-section-row-${section.type}`}>
      <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground" {...attributes} {...listeners} aria-label="drag" data-testid="section-drag-handle"><GripVertical size={16} /></button>
      <button type="button" onClick={onSelect} className="flex-1 truncate text-left font-medium">{pick(def?.label, lang) || section.type}</button>
      <div className={`flex items-center transition-opacity group-hover:opacity-100 focus-within:opacity-100 ${selected ? 'opacity-100' : 'opacity-0'}`}>
        <button type="button" onClick={onToggle} className="rounded p-1 text-muted-foreground hover:text-foreground" title="toggle" data-testid="section-toggle-visibility">{section.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
        <button type="button" onClick={onDuplicate} className="rounded p-1 text-muted-foreground hover:text-foreground" title="duplicate" data-testid="section-duplicate"><Copy size={14} /></button>
        <button type="button" onClick={onDelete} className="rounded p-1 text-muted-foreground hover:text-destructive" title="delete" data-testid="section-delete"><Trash2 size={14} /></button>
      </div>
    </div>
  );
};

function SiteEditorInner() {
  const { siteId } = useParams();
  const { company } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [site, setSite] = useState(null);
  const [pageId, setPageId] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewLang, setPreviewLang] = useState(lang);
  const [device, setDevice] = useState('desktop');
  const [frameDoc, setFrameDoc] = useState(null);
  const [motionKey, setMotionKey] = useState(0);
  const sectionEl = (id) => document.getElementById(`section-${id}`) || frameDoc?.getElementById(`section-${id}`);
  const [saveState, setSaveState] = useState('saved'); // saved | dirty | saving | error
  const [aiOpen, setAiOpen] = useState(params.get('ai') === '1');
  const [libOpen, setLibOpen] = useState(false);
  const [storeLibOpen, setStoreLibOpen] = useState(false);
  const [applyingTpl, setApplyingTpl] = useState(false);
  const applyStoreTemplate = async () => {
    setApplyingTpl(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/store-template`);
      await load();
      const created = data.created || [];
      toast.success(created.length ? (lang === 'id' ? `Halaman dibuat: ${created.join(', ')}` : `Pages created: ${created.join(', ')}`) : (lang === 'id' ? 'Halaman toko sudah ada, tidak ada yang ditambahkan' : 'Store pages already exist'));
      if (!data.ecommerce_installed) toast.message(lang === 'id' ? 'Modul E-commerce belum terinstal — instal lewat menu Aplikasi agar blok menampilkan produk.' : 'E-commerce module is not installed — install it from Apps so the blocks show products.');
      const toko = (data.pages || []).find((p) => p.slug === 'toko');
      if (toko) setPageId(toko.id);
      setStoreLibOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setApplyingTpl(false); }
  };
  const [tplOpen, setTplOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [aiLanding, setAiLanding] = useState(false);
  const [pageDialog, setPageDialog] = useState(false);
  const [newPage, setNewPage] = useState({ id: '', en: '' });
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [deletePage, setDeletePage] = useState(null);
  const [settingsPage, setSettingsPage] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copySection, setCopySection] = useState(null);
  const past = useRef([]);
  const future = useRef([]);
  const [histLen, setHistLen] = useState({ past: 0, future: 0 });
  const saveTimer = useRef(null);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const [loadError, setLoadError] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}`);
      if (!data || !Array.isArray(data.pages)) throw new Error('Data website tidak lengkap (pages kosong)');
      data.pages = data.pages.map((p) => ({ ...p, sections: Array.isArray(p.sections) ? p.sections.filter(Boolean) : [] }));
      setLoadError(null);
      setSite(data);
      const home = data.pages.find((p) => p.is_home) || data.pages[0];
      const wanted = new URLSearchParams(window.location.search).get('page');
      setPageId((cur) => (wanted && data.pages.some((p) => p.id === wanted) ? wanted : cur && data.pages.some((p) => p.id === cur) ? cur : home?.id));
      return data;
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 403) { toast.error(errMsg(e)); navigate('/website'); return null; }
      setLoadError(e?.response ? errMsg(e) : (e.message || 'Gagal memuat website'));
      return null;
    }
  }, [siteId, navigate]);
  const afterApply = async () => {
    try {
      const fresh = await load();
      if (!fresh) return null;
      setPageId((fresh.pages.find((p) => p.is_home) || fresh.pages[0])?.id);
      return fresh;
    } catch (e) { toast.error(lang === 'id' ? 'Konten diterapkan, tetapi gagal memuat ulang editor. Muat ulang halaman.' : 'Content applied but the editor failed to reload.'); return null; }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!site || params.get('ai') === '1') return;
    const empty = !site.pages.some((p) => (p.sections || []).length > 0);
    if ((site.navbar?.fresh || empty) && ['manager', 'admin'].includes(site.permissions?.website || 'admin') && !sessionStorage.getItem(`pillot_start_${site.id}`)) setStartOpen(true);
  }, [site?.id]); // eslint-disable-line
  const dismissStart = () => {
    sessionStorage.setItem(`pillot_start_${site.id}`, '1'); setStartOpen(false);
    if (site?.navbar?.fresh) { const { fresh, ...nb } = site.navbar; api.patch(`/sites/${siteId}`, { navbar: nb }).then(({ data }) => setSite((s) => ({ ...s, navbar: data.navbar }))).catch(() => {}); }
  };
  const applyLandingMenu = async (fresh) => {
    const home = fresh.pages.find((p) => p.is_home) || fresh.pages[0];
    const menu = menuFromSections(home?.sections);
    if (!menu.length) return;
    const { data } = await api.patch(`/sites/${siteId}`, { navbar: { ...(fresh.navbar || {}), fresh: undefined, layout: fresh.navbar?.layout || 'left', menu, cta_label: { id: 'Hubungi Kami', en: 'Contact Us' }, cta_link: '#hubungi-kami' } });
    setSite((s) => ({ ...s, navbar: data.navbar }));
  };

  const page = useMemo(() => site?.pages?.find((p) => p.id === pageId), [site, pageId]);
  useEffect(() => {
    if (page) {
      setSections(page.sections || []);
      setSelectedId(null);
      setSaveState('saved');
      past.current = []; future.current = []; setHistLen({ past: 0, future: 0 });
    }
  }, [page?.id]); // eslint-disable-line

  const persist = useCallback(async (secs) => {
    if (!pageId) return;
    setSaveState('saving');
    try {
      const { data } = await api.patch(`/sites/${siteId}/pages/${pageId}`, { sections: secs });
      setSite((s) => ({ ...s, pages: s.pages.map((p) => (p.id === pageId ? { ...p, sections: data.sections, updated_at: data.updated_at } : p)) }));
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      toast.error(errMsg(e));
    }
  }, [siteId, pageId]);

  const applySections = (next) => {
    setSections(next);
    setSaveState('dirty');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 1200);
  };
  const updateSections = (next) => {
    past.current = [...past.current.slice(-49), sectionsRef.current];
    future.current = [];
    setHistLen({ past: past.current.length, future: 0 });
    applySections(next);
  };
  const undo = () => { if (!past.current.length) return; const prev = past.current.pop(); future.current.push(sectionsRef.current); setHistLen({ past: past.current.length, future: future.current.length }); applySections(prev); };
  const redo = () => { if (!future.current.length) return; const nxt = future.current.pop(); past.current.push(sectionsRef.current); setHistLen({ past: past.current.length, future: future.current.length }); applySections(nxt); };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const saveNow = () => { clearTimeout(saveTimer.current); persist(sectionsRef.current); };
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); saveNow(); }
      else if ((e.metaKey || e.ctrlKey) && !typing && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if ((e.metaKey || e.ctrlKey) && !typing && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldIdx = sections.findIndex((s) => s.id === active.id);
      const newIdx = sections.findIndex((s) => s.id === over.id);
      updateSections(arrayMove(sections, oldIdx, newIdx));
    }
  };

  const addSection = (type) => {
    const s = newSection(type);
    updateSections([...sections, s]);
    setSelectedId(s.id);
    setLibOpen(false);
    setStoreLibOpen(false);
    setTimeout(() => sectionEl(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  };
  const addAiSection = async (type) => {
    setLibOpen(false);
    const toastId = toast.loading(lang === 'id' ? 'AI sedang menulis section...' : 'AI is writing the section...');
    try {
      const { data } = await api.post(`/sites/${siteId}/ai/section`, { section_type: type, context: `${site.name}. ${pick(site.seo?.description, 'id')}` });
      updateSections([...sections, data]);
      setSelectedId(data.id);
      toast.success(lang === 'id' ? 'Section dibuat oleh AI' : 'Section generated by AI', { id: toastId });
    } catch (e) {
      toast.error(errMsg(e), { id: toastId });
    }
  };
  const selected = sections.find((s) => s.id === selectedId);
  const changeSelected = (next) => updateSections(sections.map((s) => (s.id === next.id ? next : s)));

  const translateSection = async (from, to) => {
    if (!selected) return;
    setTranslating(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/ai/translate`, { payload: selected.content, source_lang: from, target_lang: to });
      changeSelected({ ...selected, content: data.payload });
      toast.success(lang === 'id' ? 'Section diterjemahkan' : 'Section translated');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTranslating(false);
    }
  };
  const translatePage = async (from, to) => {
    clearTimeout(saveTimer.current);
    await persist(sectionsRef.current);
    const toastId = toast.loading(lang === 'id' ? `Menerjemahkan seluruh halaman ${from.toUpperCase()} → ${to.toUpperCase()}...` : `Translating whole page ${from.toUpperCase()} → ${to.toUpperCase()}...`);
    try {
      const { data: job } = await api.post(`/sites/${siteId}/ai/pages/${pageId}/translate`, { payload: {}, source_lang: from, target_lang: to });
      const data = await pollAiJob(siteId, job.job_id);
      setSite((s) => ({ ...s, pages: s.pages.map((p) => (p.id === pageId ? data : p)) }));
      setSections(data.sections);
      toast.success(lang === 'id' ? 'Halaman diterjemahkan' : 'Page translated', { id: toastId });
    } catch (e) {
      toast.error(e?.response ? errMsg(e) : e.message, { id: toastId });
    }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      clearTimeout(saveTimer.current);
      if (saveState === 'dirty') await persist(sectionsRef.current);
      const { data } = await api.post(`/sites/${siteId}/${site.is_published ? 'unpublish' : 'publish'}`);
      setSite((s) => ({ ...s, ...data, pages: s.pages }));
      toast.success(data.is_published ? (lang === 'id' ? 'Website live!' : 'Website is live!') : (lang === 'id' ? 'Publikasi ditarik' : 'Unpublished'));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  const createPage = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/sites/${siteId}/pages`, { title: { id: newPage.id, en: newPage.en || newPage.id } });
      setSite((s) => ({ ...s, pages: [...s.pages, data] }));
      setPageId(data.id);
      setPageDialog(false);
      setNewPage({ id: '', en: '' });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const removePage = async () => {
    try {
      await api.delete(`/sites/${siteId}/pages/${deletePage.id}`);
      const data = await load();
      setPageId((data.pages.find((p) => p.is_home) || data.pages[0])?.id);
      setDeletePage(null);
      toast.success(lang === 'id' ? 'Halaman dihapus' : 'Page deleted');
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const onCanvasNavigate = (e, href) => {
    e?.preventDefault?.();
    if (!href) return;
    if (href.startsWith('http')) return;
    const [path, hash] = href.split('#');
    const scrollTo = () => hash && (document.getElementById(hash) || frameDoc?.getElementById(hash))?.scrollIntoView({ behavior: 'smooth' });
    if (!path) { scrollTo(); return; }
    const slug = path.replace(/^\//, '') || 'home';
    const target = site.pages.find((p) => (slug === 'home' ? p.is_home : p.slug === slug));
    if (target) { setPageId(target.id); if (hash) setTimeout(scrollTo, 300); }
  };

  if (loadError && !site) return (
    <AppShell moduleName={t('website')}>
      <div className="flex min-h-[60vh] items-center justify-center p-6" data-testid="editor-load-error">
        <div className="max-w-md rounded-2xl border border-border bg-white p-8 text-center shadow-soft">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600"><AlertTriangle size={24} /></div>
          <h2 className="font-display text-lg font-semibold">{lang === 'id' ? 'Gagal memuat website' : 'Failed to load website'}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
          <div className="mt-5 flex justify-center gap-2"><Button variant="outline" className="rounded-xl" onClick={load} data-testid="editor-load-retry"><RefreshCw size={14} className="mr-1.5" />{lang === 'id' ? 'Coba lagi' : 'Retry'}</Button><Button className="rounded-xl" onClick={() => navigate('/website')} data-testid="editor-load-back">{lang === 'id' ? 'Kembali' : 'Back'}</Button></div>
        </div>
      </div>
    </AppShell>
  );
  if (!site || !page) return <AppShell moduleName={t('website')}><div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" /></div></AppShell>;

  const siteLevel = site.permissions?.website || (['owner', 'admin'].includes(site.role) ? 'admin' : 'manager');
  const canEdit = ['manager', 'admin'].includes(siteLevel);
  const hasContent = site.pages.some((p) => (p.sections || []).length > 0);
  const previewSite = { ...site, pages: site.pages.map((p) => (p.id === pageId ? { ...p, sections } : p)) };

  const PagesList = (
    <div className="space-y-1">
      <SortablePages pages={site.pages} pageId={pageId} canEdit={canEdit} siteId={siteId}
        onSelect={(p) => { setPageId(p.id); setLeftOpen(false); }} onSettings={(p) => setSettingsPage(p)} onDelete={(p) => setDeletePage(p)}
        onReordered={(next) => setSite((s) => ({ ...s, pages: next }))} onDuplicated={(pg) => { setSite((s) => ({ ...s, pages: [...s.pages, pg] })); setPageId(pg.id); }} />
      {canEdit && <Button variant="ghost" size="sm" className="w-full justify-start rounded-lg text-muted-foreground" onClick={() => setPageDialog(true)} data-testid="website-editor-add-page-button"><Plus size={14} className="mr-1.5" />{t('add_page')}</Button>}
    </div>
  );

  const LeftPanel = (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-4 py-3"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('pages')}</div><div className="mt-2">{PagesList}</div></div>
      <div className="flex items-center justify-between px-4 pt-3 pb-2"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{t('sections')} ({sections.length})</div>
        {canEdit && <button type="button" onClick={() => setHistoryOpen(true)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="editor-version-history-button"><History size={12} />{lang === 'id' ? 'Riwayat' : 'History'}</button>}</div>
      <ScrollArea className="flex-1 px-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5 pb-3" data-testid="website-editor-section-list">
              {sections.map((s) => (
                <SortableRow key={s.id} section={s} lang={lang} selected={s.id === selectedId}
                  onSelect={() => { setSelectedId(s.id); setLeftOpen(false); sectionEl(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); if (window.innerWidth < 1024) setRightOpen(true); }}
                  onDelete={() => { updateSections(sections.filter((x) => x.id !== s.id)); if (selectedId === s.id) setSelectedId(null); }}
                  onDuplicate={() => { const i = sections.findIndex((x) => x.id === s.id); const copy = { ...JSON.parse(JSON.stringify(s)), id: rid() }; const next = [...sections]; next.splice(i + 1, 0, copy); updateSections(next); }}
                  onToggle={() => updateSections(sections.map((x) => (x.id === s.id ? { ...x, hidden: !x.hidden } : x)))} />
              ))}
              {sections.length === 0 && <div className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">{lang === 'id' ? 'Belum ada section. Tambahkan atau gunakan AI.' : 'No sections yet. Add one or use AI.'}</div>}
            </div>
          </SortableContext>
        </DndContext>
      </ScrollArea>
      {canEdit && (
        <div className="border-t border-border p-3 space-y-2">
          <Button className="w-full rounded-xl" onClick={() => setLibOpen(true)} data-testid="website-editor-add-section-button"><Plus size={16} className="mr-1.5" />{t('add_section')}</Button>
          <Button variant="outline" className="w-full rounded-xl border-orange-300 text-orange-800 hover:bg-orange-50" onClick={() => setStoreLibOpen(true)} data-testid="website-editor-add-store-block-button"><Store size={16} className="mr-1.5 text-orange-600" />{lang === 'id' ? 'Tambah Blok Toko' : 'Add Store Block'}</Button>
          <Button variant="outline" className="w-full rounded-xl border-[hsl(var(--pillot-mint))]/40 text-[hsl(var(--pillot-navy))]" onClick={() => setAiOpen(true)} data-testid="website-editor-ai-generate-button"><Sparkles size={16} className="mr-1.5 text-[hsl(var(--pillot-mint))]" />{t('generate_with_ai')}</Button>
        </div>
      )}
    </div>
  );

  const Inspector = selected ? (
    <SectionInspector key={selected.id} section={selected} onChange={changeSelected} siteId={siteId} onTranslateSection={translateSection} translating={translating}
      onDelete={canEdit ? () => { updateSections(sections.filter((x) => x.id !== selected.id)); setSelectedId(null); } : undefined}
      onDuplicate={canEdit ? () => { const i = sections.findIndex((x) => x.id === selected.id); const copy = { ...JSON.parse(JSON.stringify(selected)), id: rid() }; const next = [...sections]; next.splice(i + 1, 0, copy); updateSections(next); } : undefined}
      onCopyToPage={canEdit && site.pages.length > 1 ? () => setCopySection(selected) : undefined} />
  ) : (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center text-sm text-muted-foreground" data-testid="inspector-empty">
      <PanelRight className="mb-3 opacity-40" />
      {lang === 'id' ? 'Pilih section di kanvas atau daftar untuk mengedit kontennya.' : 'Select a section on the canvas or list to edit its content.'}
    </div>
  );

  const saveLabel = { saved: t('saved'), dirty: t('unsaved'), saving: lang === 'id' ? 'Menyimpan...' : 'Saving...', error: lang === 'id' ? 'Gagal simpan' : 'Save failed' }[saveState];

  return (
    <AppShell fullBleed moduleName={t('website')} moduleIcon={<ModuleIcon moduleKey="website" size="sm" />}
      actions={
        <div className="flex items-center gap-1.5">
          {canEdit && <div className="hidden sm:inline-flex rounded-lg border border-border bg-white p-0.5 mr-1" data-testid="editor-undo-redo">
            <button type="button" onClick={undo} disabled={!histLen.past} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Undo (Ctrl+Z)" data-testid="editor-undo"><Undo2 size={14} /></button>
            <button type="button" onClick={redo} disabled={!histLen.future} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Redo (Ctrl+Shift+Z)" data-testid="editor-redo"><Redo2 size={14} /></button>
          </div>}
          <span className={`hidden md:inline-flex items-center gap-1 text-xs ${saveState === 'error' ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="editor-save-state">{saveState === 'saving' ? <Loader2 size={12} className="animate-spin" /> : saveState === 'saved' ? <Check size={12} /> : null}{saveLabel}</span>
          {saveState === 'dirty' && <Button size="sm" variant="outline" className="rounded-lg" onClick={saveNow} data-testid="website-editor-save-button">{t('save')}</Button>}
          <Button size="sm" variant="ghost" className="rounded-lg" asChild><a href={`/site/${site.slug}?preview=1&lang=${previewLang}`} target="_blank" rel="noreferrer" data-testid="website-editor-preview-link"><ExternalLink size={14} className="mr-1" /><span className="hidden sm:inline">{t('preview')}</span></a></Button>
          <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => navigate(`/website/${siteId}/settings`)} data-testid="website-editor-settings-button"><Settings size={14} className="mr-1" /><span className="hidden sm:inline">{t('settings')}</span></Button>
          {canEdit && <Button size="sm" className={`rounded-lg ${site.is_published ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={togglePublish} disabled={publishing} data-testid="website-editor-publish-button">{publishing ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} className="mr-1" />}{site.is_published ? t('unpublish') : t('publish')}</Button>}
        </div>
      }>
      <div className="flex flex-1 min-h-0">
        <aside className="hidden lg:flex w-[280px] shrink-0 flex-col border-r border-border bg-white">{LeftPanel}</aside>
        <section className="flex flex-1 min-w-0 flex-col bg-[hsl(var(--pillot-sand))]">
          <div className="flex items-center gap-2 border-b border-border bg-white/70 px-3 py-2">
            <Button variant="ghost" size="sm" className="lg:hidden rounded-lg" onClick={() => setLeftOpen(true)} data-testid="editor-open-left-panel"><PanelLeft size={16} /></Button>
            <Link to="/website" className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ChevronLeft size={14} />{t('my_sites')}</Link>
            <span className="hidden sm:inline text-muted-foreground">/</span>
            <span className="text-sm font-medium truncate">{site.name}</span>
            <Badge variant="outline" className={`rounded-full text-[10px] ${site.is_published ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : ''}`} data-testid="editor-publish-status">{site.is_published ? t('published') : t('draft')}</Badge>
            <div className="flex-1" />
            <TooltipProvider>
              <div className="hidden sm:inline-flex rounded-lg border border-border bg-white p-0.5" data-testid="editor-device-toggle">
                {[['desktop', Monitor], ['tablet', Tablet], ['mobile', Smartphone]].map(([d, I]) => (
                  <Tooltip key={d}><TooltipTrigger asChild><button type="button" onClick={() => setDevice(d)} className={`rounded-md p-1.5 ${device === d ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`editor-device-${d}`}><I size={14} /></button></TooltipTrigger><TooltipContent>{t(d)}</TooltipContent></Tooltip>
                ))}
              </div>
              <Tooltip><TooltipTrigger asChild><button type="button" onClick={() => setMotionKey((k) => k + 1)} className="hidden sm:inline-flex rounded-lg border border-border bg-white p-2 text-muted-foreground hover:text-foreground" data-testid="editor-replay-motion"><RotateCcw size={14} /></button></TooltipTrigger><TooltipContent>{lang === 'id' ? 'Putar ulang animasi' : 'Replay animations'}</TooltipContent></Tooltip>
            </TooltipProvider>
            <div className="inline-flex rounded-lg border border-border bg-white p-0.5 text-xs font-semibold" data-testid="editor-preview-lang">
              {(site.languages || ['id', 'en']).map((l) => <button key={l} type="button" onClick={() => setPreviewLang(l)} className={`rounded-md px-2 py-1 uppercase ${previewLang === l ? 'bg-primary text-white' : 'text-muted-foreground'}`} data-testid={`editor-preview-lang-${l}`}>{l}</button>)}
            </div>
            {canEdit && <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setTplOpen(true)} data-testid="editor-template-button"><LayoutTemplate size={14} className="mr-1" /><span className="hidden sm:inline">Template</span></Button>}
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="rounded-lg" data-testid="editor-ai-menu"><Sparkles size={14} className="mr-1 text-[hsl(var(--pillot-mint))]" /><span className="hidden sm:inline">AI</span></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>{t('ai_assistant')}</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setAiOpen(true)} data-testid="editor-ai-generate-site"><Sparkles size={14} className="mr-2" />{t('generate_with_ai')}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => translatePage('id', 'en')} data-testid="editor-ai-translate-id-en"><Languages size={14} className="mr-2" />{lang === 'id' ? 'Terjemahkan halaman ID → EN' : 'Translate page ID → EN'}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => translatePage('en', 'id')} data-testid="editor-ai-translate-en-id"><Languages size={14} className="mr-2" />{lang === 'id' ? 'Terjemahkan halaman EN → ID' : 'Translate page EN → ID'}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="sm" className="lg:hidden rounded-lg" onClick={() => setRightOpen(true)} data-testid="editor-open-right-panel"><PanelRight size={16} /></Button>
          </div>
          <div className="flex-1 overflow-auto p-3 sm:p-5 scrollbar-thin">
            <DeviceFrame device={device} onDocument={setFrameDoc}>
              <div className={device === 'desktop' ? 'mx-auto overflow-hidden rounded-2xl border border-border bg-white shadow-soft' : 'min-h-full'} data-testid="editor-canvas">
                <SiteRenderer site={previewSite} page={{ ...page, sections }} lang={previewLang} setLang={setPreviewLang} onNavigate={onCanvasNavigate} isPreview storeSlug={company?.slug} selectedId={selectedId} motionKey={motionKey} onSelectSection={canEdit ? (id) => { setSelectedId(id); if (window.innerWidth < 1024) setRightOpen(true); } : undefined} />
              </div>
            </DeviceFrame>
          </div>
        </section>
        <aside className="hidden lg:flex w-[360px] shrink-0 flex-col border-l border-border bg-white">{canEdit ? Inspector : <div className="p-6 text-sm text-muted-foreground">{lang === 'id' ? 'Anda hanya memiliki akses lihat.' : 'You have view-only access.'}</div>}</aside>
      </div>

      <Sheet open={leftOpen} onOpenChange={setLeftOpen}><SheetContent side="left" className="w-[300px] p-0">{LeftPanel}</SheetContent></Sheet>
      <Sheet open={rightOpen} onOpenChange={setRightOpen}><SheetContent side="right" className="w-[360px] p-0 sm:max-w-[360px]">{Inspector}</SheetContent></Sheet>

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{t('add_section')}</DialogTitle><DialogDescription>{lang === 'id' ? 'Pilih blok untuk ditambahkan ke halaman ini. Klik ikon AI agar konten langsung ditulis oleh AI.' : 'Pick a block to add to this page. Click the AI icon to have the content written by AI.'}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
            {SECTION_ORDER.filter((type) => !STORE_SECTION_TYPES.includes(type)).map((type) => (
              <div key={type} className="group relative rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-primary hover:bg-secondary/40">
                <button type="button" onClick={() => addSection(type)} className="w-full text-left" data-testid={`section-library-${type}`}>
                  <div className="font-medium text-sm">{pick(SECTION_DEFS[type].label, lang)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{pick(SECTION_DEFS[type].desc, lang)}</div>
                </button>
                <button type="button" onClick={() => addAiSection(type)} className="absolute right-2 top-2 rounded-md p-1 text-[hsl(var(--pillot-mint))] opacity-70 hover:bg-secondary hover:opacity-100" title="AI" data-testid={`section-library-ai-${type}`}><Sparkles size={14} /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={storeLibOpen} onOpenChange={setStoreLibOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="store-block-library">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Store size={18} className="text-orange-600" />{lang === 'id' ? 'Tambah Blok Toko' : 'Add Store Block'}</DialogTitle><DialogDescription>{lang === 'id' ? 'Blok E-commerce: produk, keranjang & checkout (Xendit + RajaOngkir), dan akun pelanggan. Mengikuti tema website secara otomatis. Pastikan modul E-commerce terinstal dan toko sudah diatur.' : 'E-commerce blocks: products, cart & checkout (Xendit + RajaOngkir) and customer account. They follow the site theme automatically. Make sure the E-commerce module is installed and the store is configured.'}</DialogDescription></DialogHeader>
          <button type="button" onClick={applyStoreTemplate} disabled={applyingTpl || !canEdit} className="flex w-full items-center gap-4 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4 text-left transition-colors hover:border-orange-400 disabled:opacity-60" data-testid="store-template-apply">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-600 text-white">{applyingTpl ? <Loader2 size={18} className="animate-spin" /> : <LayoutTemplate size={18} />}</span>
            <span><span className="block text-sm font-semibold">{lang === 'id' ? 'Pasang paket halaman "Toko Online" sekali klik' : 'One-click "Online Store" page pack'}</span><span className="block text-xs text-muted-foreground">{lang === 'id' ? 'Membuat halaman Toko (banner, keunggulan, kategori, slider, katalog), Keranjang & Checkout, dan Akun — plus menu navbar otomatis. Halaman yang sudah ada tidak diubah.' : 'Creates Shop (banner, benefits, categories, carousel, catalog), Cart & Checkout and Account pages — plus navbar menu. Existing pages are untouched.'}</span></span>
          </button>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{lang === 'id' ? 'Atau tambah satu blok' : 'Or add a single block'}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto pr-1">
            {STORE_SECTION_TYPES.map((type) => (
              <button type="button" key={type} onClick={() => addSection(type)} className="rounded-xl border border-border bg-white p-4 text-left transition-colors hover:border-orange-400 hover:bg-orange-50/40" data-testid={`store-block-library-${type}`}>
                <div className="font-medium text-sm">{pick(SECTION_DEFS[type].label, lang)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{pick(SECTION_DEFS[type].desc, lang)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pageDialog} onOpenChange={setPageDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t('add_page')}</DialogTitle></DialogHeader>
          <form onSubmit={createPage} className="space-y-4">
            <div className="space-y-1.5"><Label>{lang === 'id' ? 'Judul (ID)' : 'Title (ID)'}</Label><Input required value={newPage.id} onChange={(e) => setNewPage({ ...newPage, id: e.target.value })} className="rounded-xl" data-testid="new-page-title-id" /></div>
            <div className="space-y-1.5"><Label>{lang === 'id' ? 'Judul (EN)' : 'Title (EN)'}</Label><Input value={newPage.en} onChange={(e) => setNewPage({ ...newPage, en: e.target.value })} className="rounded-xl" data-testid="new-page-title-en" /></div>
            <DialogFooter><Button type="button" variant="outline" className="rounded-xl" onClick={() => setPageDialog(false)}>{t('cancel')}</Button><Button type="submit" className="rounded-xl" data-testid="new-page-submit">{t('create')}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePage} onOpenChange={(o) => !o && setDeletePage(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle><AlertDialogDescription>{pick(deletePage?.title, lang)}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={removePage} className="bg-destructive hover:bg-destructive/90" data-testid="page-delete-confirm">{t('yes_delete')}</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      <PageSettingsDialog page={settingsPage} open={!!settingsPage} onOpenChange={(o) => !o && setSettingsPage(null)} siteId={siteId} onSaved={(pg) => setSite((s) => ({ ...s, pages: s.pages.map((p) => (p.id === pg.id ? { ...p, ...pg, sections: p.sections } : p)) }))} />
      <VersionHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} siteId={siteId} pageId={pageId} onRestored={(pg) => { setSite((s) => ({ ...s, pages: s.pages.map((p) => (p.id === pg.id ? pg : p)) })); setSections(pg.sections || []); setSaveState('saved'); }} />
      <CopySectionDialog open={!!copySection} onOpenChange={(o) => !o && setCopySection(null)} siteId={siteId} pages={site.pages} currentPageId={pageId} section={copySection} onCopied={(pg) => setSite((s) => ({ ...s, pages: s.pages.map((p) => (p.id === pg.id ? pg : p)) }))} />
      <AiGenerateDialog open={aiOpen} onOpenChange={(o) => { setAiOpen(o); if (!o) setAiLanding(false); if (!o && params.get('ai')) { params.delete('ai'); setParams(params, { replace: true }); } }} site={site} companyName={company?.name} hasContent={hasContent} landing={aiLanding}
        onApplied={async () => { const fresh = await afterApply(); if (fresh && aiLanding) await applyLandingMenu(fresh).catch(() => {}); }} />
      <TemplateGallery open={tplOpen} onOpenChange={setTplOpen} site={site} hasContent={hasContent && !site.navbar?.fresh} onApplied={afterApply} />
      <StartDialog open={startOpen} onSkip={dismissStart} onTemplate={() => { dismissStart(); setTplOpen(true); }} onAi={() => { dismissStart(); setAiLanding(true); setAiOpen(true); }} />
    </AppShell>
  );
}

export default function SiteEditorPage() {
  const { siteId } = useParams();
  return <ErrorBoundary resetKey={siteId} title="Editor gagal ditampilkan / Editor crashed" hint="Kemungkinan konten website berisi data yang tidak valid. Muat ulang; jika masih gagal, buka Pengaturan website dan periksa section terakhir yang diubah."><SiteEditorInner /></ErrorBoundary>;
}
